<template>
  <div class="page-content degree-apply">
    <div class="page-header">
      <div class="page-header-line"></div>
      <h5>学位申请</h5>
    </div>

    <div class="page-body">
      <!-- 批次 -->
      <div class="batch-row">
        <div class="batch-item">
          <span class="field-label required-label">学位申请批次：</span>
          <el-select
            v-model="batchId"
            placeholder="请选择学位申请批次"
            class="batch-select"
            :disabled="isThesisOnlyEdit"
            @change="onBatchChange"
          >
            <el-option
              v-for="item in batchOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </div>
        <div class="batch-item batch-time">
          <span class="field-label">{{ submitTimeLabel }}：</span>
          <span class="field-value">{{ submitTimeText }}</span>
        </div>
      </div>

      <div v-if="currentBatch && !isInSubmitTime" class="time-tip">
        {{ submitTimeLabel }}为 {{ submitTimeText }}，当前不在可提交时间范围内
      </div>

      <!-- 暂不通过 / 论文打回修改：展示审核结果与描述 -->
      <div v-if="isEdit" class="audit-result-box">
        <div class="audit-result-row">
          <span class="audit-label">状态：</span>
          <span class="status-text status-fail">{{ auditStatusText }}</span>
        </div>
        <div class="audit-result-row">
          <span class="audit-label">描述：</span>
          <span class="audit-desc">{{ auditRemark || '—' }}</span>
        </div>
      </div>

      <div class="divider"></div>

      <el-form
        ref="form"
        :model="form"
        :rules="rules"
        label-width="160px"
        class="apply-form"
        label-position="right"
        :disabled="!isInSubmitTime"
      >
        <!-- 学位课程成绩与考籍表 -->
        <div class="section-title">学位课程成绩与考籍表</div>
        <el-form-item label="学位课程平均成绩：" prop="examAvgScore">
          <el-input
            v-model="form.examAvgScore"
            placeholder="请输入平均成绩"
            class="form-input"
            :disabled="isThesisOnlyEdit"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="考籍表材料：" prop="studentStatusProofUrl">
          <div class="upload-row">
            <UploadImage
              v-if="!isThesisOnlyEdit"
              mode="button"
              button-text="上传考籍表"
              accept=".jpg,.jpeg,.png"
              v-model="form.studentStatusProofUrl"
              @success="onImageSuccess($event, 'studentStatusProof')"
            />
            <span v-if="!isThesisOnlyEdit" class="upload-tip">仅支持上传图片格式（JPG/PNG/JPEG）</span>
          </div>
          <a
            v-if="form.studentStatusProofName"
            class="file-link"
            :href="form.studentStatusProofUrl"
            target="_blank"
          >{{ form.studentStatusProofName }}</a>
        </el-form-item>

        <div class="divider"></div>

        <!-- 学位外语成绩 -->
        <div class="section-title">学位外语成绩</div>
        <el-form-item label="" prop="eflSource" class="radio-item" label-width="0">
          <el-radio-group v-model="form.eflSource" :disabled="isThesisOnlyEdit" @change="onEflSourceChange">
            <el-radio :label="1">学位外语</el-radio>
            <el-radio :label="2">其他</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 学位外语 -->
        <template v-if="form.eflSource === 1">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="外语类型：" prop="eflType">
                <el-select
                  v-model="form.eflType"
                  placeholder="请选择外语类型"
                  class="form-input-full"
                  :disabled="isThesisOnlyEdit"
                  clearable
                >
                  <el-option
                    v-for="(label, value) in eflTypeOptions"
                    :key="value"
                    :label="label"
                    :value="value"
                  ></el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="学位外语成绩：" prop="eflScore">
                <el-input
                  v-model="form.eflScore"
                  placeholder="请输入学位外语成绩"
                  class="form-input-full"
                  :disabled="isThesisOnlyEdit"
                  clearable
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="考试时间：" prop="eflExamDate">
                <el-date-picker
                  v-model="form.eflExamDate"
                  type="date"
                  placeholder="请选择考试时间"
                  value-format="yyyy-MM-dd"
                  class="form-input-full"
                  :disabled="isThesisOnlyEdit"
                ></el-date-picker>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="学位外语证号：" prop="eflCertificateNo">
                <el-input
                  v-model="form.eflCertificateNo"
                  placeholder="请输入证书编号"
                  class="form-input-full"
                  :disabled="isThesisOnlyEdit"
                  clearable
                ></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="考试地点：" prop="eflExamAddress">
            <el-input
              v-model="form.eflExamAddress"
              placeholder="请输入考试地点"
              class="form-input-wide"
              :disabled="isThesisOnlyEdit"
              clearable
            ></el-input>
          </el-form-item>
          <el-form-item label="成绩证明材料：" prop="achievementProofUrl">
            <div class="upload-row">
              <UploadImage
                v-if="!isThesisOnlyEdit"
                mode="button"
                button-text="上传证明"
                accept=".jpg,.jpeg,.png"
                v-model="form.achievementProofUrl"
                @success="onImageSuccess($event, 'achievementProof')"
              />
              <span v-if="!isThesisOnlyEdit" class="upload-tip">仅支持上传图片格式（JPG/PNG/JPEG）</span>
            </div>
            <a
              v-if="form.achievementProofName"
              class="file-link"
              :href="form.achievementProofUrl"
              target="_blank"
            >{{ form.achievementProofName }}</a>
          </el-form-item>
        </template>

        <!-- 其他 -->
        <template v-else>
          <el-form-item label="成果获取时间：" prop="eflExamDate">
            <el-date-picker
              v-model="form.eflExamDate"
              type="date"
              placeholder="请选择考试时间"
              value-format="yyyy-MM-dd"
              class="form-input"
              :disabled="isThesisOnlyEdit"
            ></el-date-picker>
          </el-form-item>
          <el-form-item label="成绩证明材料：" prop="achievementProofUrl">
            <div class="upload-row">
              <UploadImage
                v-if="!isThesisOnlyEdit"
                mode="button"
                button-text="上传证明"
                accept=".jpg,.jpeg,.png"
                v-model="form.achievementProofUrl"
                @success="onImageSuccess($event, 'achievementProof')"
              />
              <span v-if="!isThesisOnlyEdit" class="upload-tip">仅支持上传图片格式（JPG/PNG/JPEG）</span>
            </div>
            <a
              v-if="form.achievementProofName"
              class="file-link"
              :href="form.achievementProofUrl"
              target="_blank"
            >{{ form.achievementProofName }}</a>
          </el-form-item>
        </template>

        <div class="divider"></div>

        <!-- 论文 -->
        <div class="section-title">论文</div>
        <el-form-item label="论文题目：" prop="thesisTitle">
          <el-input
            v-model="form.thesisTitle"
            placeholder="请输入论文题目"
            class="form-input-wide"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="重复率：" prop="plagiarismRate">
          <el-input
            v-model="form.plagiarismRate"
            placeholder="请输入重复率（%）"
            class="form-input-wide"
            clearable
          ></el-input>
        </el-form-item>
        <el-form-item label="论文稿件：" prop="thesisManuscriptUrl">
          <div class="upload-row">
            <Upload
              button-text="上传论文稿件"
              accept=".pdf,.doc,.docx"
              url="/student/thesis2/uploadFiles"
              @success="onUpload($event, 'thesisManuscript')"
            />
          </div>
          <a
            v-if="form.thesisManuscriptName"
            class="file-link"
            :href="form.thesisManuscriptUrl"
            target="_blank"
          >{{ form.thesisManuscriptName }}</a>
        </el-form-item>
        <el-form-item label="查重报告：" prop="plagiarismReportUrl">
          <div class="upload-row">
            <Upload
              button-text="上传查重报告"
              accept=".pdf"
              url="/student/thesis2/uploadFiles"
              @success="onUpload($event, 'plagiarismReport')"
            />
            <span class="upload-tip">仅支持 PDF格式</span>
          </div>
          <a
            v-if="form.plagiarismReportName"
            class="file-link"
            :href="form.plagiarismReportUrl"
            target="_blank"
          >{{ form.plagiarismReportName }}</a>
        </el-form-item>
        <el-form-item label="其他附件：" prop="otherAttachmentUrl">
          <div class="upload-row">
            <Upload
              button-text="上传其他附件包"
              accept=".zip"
              url="/student/thesis2/uploadFiles"
              @success="onUpload($event, 'otherAttachment')"
            />
            <span class="upload-tip">请将其他相关附件（如授权书、知情同意书等）压缩为一个 ZIP 加密包上传</span>
          </div>
          <a
            v-if="form.otherAttachmentName"
            class="file-link"
            :href="form.otherAttachmentUrl"
            target="_blank"
          >{{ form.otherAttachmentName }}</a>
        </el-form-item>

        <div class="divider"></div>

        <!-- 个人电子照片 -->
        <div class="section-title">个人电子照片</div>
        <el-form-item label="上传照片：" prop="electronicPhotoUrl">
          <div class="upload-row">
            <UploadImage
              v-if="!isThesisOnlyEdit"
              mode="button"
              button-text="上传照片"
              accept=".jpg,.jpeg,.png"
              :size="500 * 1024"
              v-model="form.electronicPhotoUrl"
              @success="onImageSuccess($event, 'electronicPhoto')"
            />
            <span v-if="!isThesisOnlyEdit" class="upload-tip">要求：蓝底二寸照，JPG/PNG/JPEG格式，大小不超过500KB</span>
          </div>
          <a
            v-if="form.electronicPhotoName"
            class="file-link"
            :href="form.electronicPhotoUrl"
            target="_blank"
          >{{ form.electronicPhotoName }}</a>
        </el-form-item>
      </el-form>

      <div class="action-row" v-if="isInSubmitTime">
        <el-button @click="onCancel">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="onSubmit">
          {{ isEdit ? '重新提交' : '提交' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { degreeApplyStatus, degreeEflType, degreeThesisEnrollStatus } from '@/utils/enums'

export default {
  name: 'DegreeApply',
  components: {
    Upload: () => import('@/components/Upload.vue'),
    UploadImage: () => import('@/components/UploadImage.vue'),
  },
  data() {
    const requireUpload = (msg) => ({
      required: true,
      message: msg,
      trigger: 'change',
    })
    return {
      batchId: '',
      batchOptions: [],
      currentBatch: null,
      submitting: false,
      isEdit: false,
      applicationId: '',
      applyStatus: null,
      enrollStatus: null,
      // 暂不通过来源：0 初审打回、1 终审打回
      rejectFromStatus: 0,
      auditRemark: '',
      eflTypeOptions: degreeEflType,
      form: {
        examAvgScore: '',
        studentStatusProofUrl: '',
        studentStatusProofName: '',
        eflSource: 1,
        eflType: '',
        eflScore: '',
        eflExamDate: '',
        eflCertificateNo: '',
        eflExamAddress: '',
        achievementProofUrl: '',
        achievementProofName: '',
        thesisTitle: '',
        plagiarismRate: '',
        thesisManuscriptUrl: '',
        thesisManuscriptName: '',
        plagiarismReportUrl: '',
        plagiarismReportName: '',
        otherAttachmentUrl: '',
        otherAttachmentName: '',
        electronicPhotoUrl: '',
        electronicPhotoName: '',
      },
      rules: {
        examAvgScore: [{ required: true, message: '请输入学位课程平均成绩', trigger: 'blur' }],
        studentStatusProofUrl: [requireUpload('请上传考籍表材料')],
        eflSource: [{ required: true, message: '请选择外语成绩来源', trigger: 'change' }],
        eflType: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (this.form.eflSource === 1 && !value) {
                callback(new Error('请选择外语类型'))
              } else {
                callback()
              }
            },
            trigger: 'change',
          },
        ],
        eflScore: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if (this.form.eflSource === 1 && !value) {
                callback(new Error('请输入学位外语成绩'))
              } else {
                callback()
              }
            },
            trigger: 'blur',
          },
        ],
        eflExamDate: [{ required: true, message: '请选择时间', trigger: 'change' }],
        // eflCertificateNo: [
        //   {
        //     required: true,
        //     validator: (rule, value, callback) => {
        //       if (this.form.eflSource === 1 && !value) {
        //         callback(new Error('请输入学位外语证号'))
        //       } else {
        //         callback()
        //       }
        //     },
        //     trigger: 'blur',
        //   },
        // ],
        // eflExamAddress: [
        //   {
        //     required: true,
        //     validator: (rule, value, callback) => {
        //       if (this.form.eflSource === 1 && !value) {
        //         callback(new Error('请输入考试地点'))
        //       } else {
        //         callback()
        //       }
        //     },
        //     trigger: 'blur',
        //   },
        // ],
        achievementProofUrl: [requireUpload('请上传成绩证明材料')],
        thesisTitle: [{ required: true, message: '请输入论文题目', trigger: 'blur' }],
        plagiarismRate: [{ required: true, message: '请输入重复率', trigger: 'blur' }],
        thesisManuscriptUrl: [requireUpload('请上传论文稿件')],
        plagiarismReportUrl: [requireUpload('请上传查重报告')],
        otherAttachmentUrl: [requireUpload('请上传其他附件')],
        electronicPhotoUrl: [requireUpload('请上传个人电子照片')],
      },
    }
  },
  computed: {
    submitTimeLabel() {
      if (this.enrollStatus === 1) return '论文重新提交截止时间'
      if (this.applyStatus === 3 && this.rejectFromStatus === 1) return '终审重新提交截止时间'
      if (this.applyStatus === 3) return '初审重新提交截止时间'
      return '资料提交时间'
    },
    submitTimeText() {
      const b = this.currentBatch
      if (!b) return '—'
      if (this.enrollStatus === 1 || this.applyStatus === 3) {
        return this.resubmitDeadline || '—'
      }
      return this.formatApplyRange(b)
    },
    /** 当前场景对应的重提截止时间 */
    resubmitDeadline() {
      const b = this.currentBatch
      if (!b) return ''
      if (this.enrollStatus === 1) return b.thesisResubmitEnd
      if (this.applyStatus === 3) {
        return this.rejectFromStatus === 1 ? b.finalReviewResubmitEnd : b.firstReviewResubmitEnd
      }
      return ''
    },
    isInSubmitTime() {
      const b = this.currentBatch
      if (!b) return false
      // 论文打回 / 资料暂不通过：按对应重提截止时间，未配置则不限制
      if (this.enrollStatus === 1 || this.applyStatus === 3) {
        return this.isBeforeEnd(this.resubmitDeadline)
      }
      if (!b.applyStartTime || !b.applyEndTime) return false
      const now = Date.now()
      return now >= this.$utils.toTime(b.applyStartTime) && now <= this.$utils.toTime(b.applyEndTime)
    },
    /** 论文打回修改：仅允许改论文相关字段 */
    isThesisOnlyEdit() {
      return this.enrollStatus === 1
    },
    auditStatusText() {
      if (this.applyStatus === 3) return '资料被打回（暂不通过）'
      if (this.enrollStatus === 1) return '论文被打回（打回修改）'
      if (this.applyStatus != null) {
        const key = String(this.applyStatus)
        return degreeApplyStatus[key] != null ? degreeApplyStatus[key] : key
      }
      if (this.enrollStatus != null) {
        const key = String(this.enrollStatus)
        return degreeThesisEnrollStatus[key] != null ? degreeThesisEnrollStatus[key] : key
      }
      return '—'
    },
  },
  created() {
    if (this.$route.query.batchId) {
      this.batchId = this.$route.query.batchId
    }
    this.initPage()
  },
  methods: {
    async initPage() {
      await this.loadBatches()
      if (this.batchId) {
        await this.checkExisting()
      }
    },
    getApplyStatus(res) {
      if (res.status != null) return Number(res.status)
      if (res.applicationStatus != null) return Number(res.applicationStatus)
      return null
    },
    getEnrollStatus(res) {
      if (res.enrollStatus != null) return Number(res.enrollStatus)
      return null
    },
    /** 暂不通过或论文打回修改：停留填写页，修改后重新提交 */
    canResubmit(res) {
      return this.getApplyStatus(res) === 3 || this.getEnrollStatus(res) === 1
    },
    pickTime(obj, camel, snake) {
      if (!obj) return ''
      return obj[camel] || obj[snake] || ''
    },
    mapBatch(c) {
      return {
        id: c.id,
        name: c.name,
        applyStartTime: this.pickTime(c, 'applyStartTime', 'apply_start_time'),
        applyEndTime: this.pickTime(c, 'applyEndTime', 'apply_end_time'),
        firstReviewResubmitEnd: this.pickTime(c, 'firstReviewResubmitEnd', 'first_review_resubmit_end'),
        finalReviewResubmitEnd: this.pickTime(c, 'finalReviewResubmitEnd', 'final_review_resubmit_end'),
        thesisResubmitEnd: this.pickTime(c, 'thesisResubmitEnd', 'thesis_resubmit_end'),
        resultOpenTime: this.pickTime(c, 'resultOpenTime', 'result_open_time'),
        academicReviewStart: this.pickTime(c, 'academicReviewStart', 'academic_review_start'),
        academicReviewEnd: this.pickTime(c, 'academicReviewEnd', 'academic_review_end'),
      }
    },
    formatApplyRange(b) {
      if (!b || (!b.applyStartTime && !b.applyEndTime)) return '—'
      return `${b.applyStartTime || ''} 至 ${b.applyEndTime || ''}`
    },
    /** 截止时间未配置时不限制；已配置则须未过期 */
    isBeforeEnd(end) {
      if (!end) return true
      return Date.now() <= this.$utils.toTime(end)
    },
    /** 从详情或日志判断暂不通过来自初审(0)还是终审(1) */
    resolveRejectFromStatus(res, logs) {
      if (res) {
        if (res.rejectFromStatus != null && res.rejectFromStatus !== '') {
          return Number(res.rejectFromStatus)
        }
        if (res.lastBeforeStatus != null && res.lastBeforeStatus !== '') {
          return Number(res.lastBeforeStatus)
        }
        if (res.beforeStatus != null && res.beforeStatus !== '') {
          return Number(res.beforeStatus)
        }
      }
      const list = logs || []
      const hit = list.find((item) => {
        const s = item.afterStatus != null ? item.afterStatus : item.status
        return Number(s) === 3
      })
      if (hit && hit.beforeStatus != null && hit.beforeStatus !== '') {
        return Number(hit.beforeStatus)
      }
      return 0
    },
    async loadBatches() {
      const res = await this.$http.post('/student/degree/myThesis', {
        thesisBatchId: this.batchId || '',
      })
      if (res && res.configs && res.configs.length) {
        this.batchOptions = res.configs.map((c) => this.mapBatch(c))
        const queryBatchId = this.$route.query.batchId
        const defaultId =
          (queryBatchId && this.batchOptions.some((b) => b.id === queryBatchId) && queryBatchId) ||
          res.defaultThesisBatchId ||
          this.batchOptions[0].id
        this.batchId = defaultId
        this.currentBatch =
          this.batchOptions.find((b) => b.id === defaultId) || this.batchOptions[0]
      } else if (res && res.config) {
        this.batchOptions = [this.mapBatch(res.config)]
        this.batchId = this.$route.query.batchId || res.config.id
        this.currentBatch =
          this.batchOptions.find((b) => b.id === this.batchId) || this.batchOptions[0]
      }
    },
    fillFormFromDetail(res) {
      this.isEdit = true
      this.applicationId = res.id || ''
      this.applyStatus = this.getApplyStatus(res)
      this.enrollStatus = this.getEnrollStatus(res)
      this.rejectFromStatus = this.resolveRejectFromStatus(res)
      this.auditRemark =
        res.remark ||
        res.operDesc ||
        res.operRemark ||
        res.auditRemark ||
        res.checkRemark ||
        ''
      this.form = {
        examAvgScore: res.examAvgScore || '',
        studentStatusProofUrl: res.studentStatusProofUrl || '',
        studentStatusProofName: res.studentStatusProofName || '',
        eflSource: res.eflSource != null ? Number(res.eflSource) : 1,
        eflType: res.eflType != null ? String(res.eflType) : '',
        eflScore: res.eflScore != null ? String(res.eflScore) : '',
        eflExamDate: res.eflExamDate || '',
        eflCertificateNo: res.eflCertificateNo || '',
        eflExamAddress: res.eflExamAddress || '',
        achievementProofUrl: res.achievementProofUrl || '',
        achievementProofName: res.achievementProofName || '',
        thesisTitle: res.thesisTitle || res.thesisDegreeName || '',
        plagiarismRate: res.plagiarismRate != null ? String(res.plagiarismRate) : '',
        thesisManuscriptUrl: res.thesisManuscriptUrl || res.thesisDegreeUrl || '',
        thesisManuscriptName: res.thesisManuscriptName || res.thesisDegreeFileName || '',
        plagiarismReportUrl: res.plagiarismReportUrl || '',
        plagiarismReportName: res.plagiarismReportName || '',
        otherAttachmentUrl: res.otherAttachmentUrl || '',
        otherAttachmentName: res.otherAttachmentName || '',
        electronicPhotoUrl: res.electronicPhotoUrl || '',
        electronicPhotoName: res.electronicPhotoName || '',
      }
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    async loadAuditRemarkFromLogs(res) {
      const enrollId = res.enrollId || res.thesisDegreeEnrollId
      if (!enrollId) return
      try {
        const logRes = await this.$http.get('/student/degree/log/detail/' + enrollId)
        const logs = (logRes && logRes.logs) || []
        this.rejectFromStatus = this.resolveRejectFromStatus(res, logs)
        if (this.auditRemark) return
        const hit =
          this.applyStatus === 3
            ? logs.find((item) => {
                const s = item.afterStatus != null ? item.afterStatus : item.status
                return Number(s) === 3
              }) || logs[0]
            : logs[0]
        if (hit) {
          this.auditRemark = hit.operRemark || hit.operDesc || hit.remark || ''
        }
      } catch (e) {
        // ignore
      }
    },
    async checkExisting() {
      try {
        const res = await this.$http.get('/student/thesis/degree/application/detail', {
          thesisBatchId: this.batchId,
        })
        if (!(res && (res.id || res.examAvgScore || res.thesisTitle))) return

        // 暂不通过 / 论文打回修改：停留填写页回填，可修改后重新提交
        if (this.canResubmit(res)) {
          this.fillFormFromDetail(res)
          await this.loadAuditRemarkFromLogs(res)
          return
        }

        this.$router.replace({
          path: '/main/degreeApplyResult',
          query: { batchId: this.batchId },
        })
      } catch (e) {
        // 无申请记录，停留填写页
      }
    },
    onBatchChange(id) {
      this.currentBatch = this.batchOptions.find((b) => b.id === id) || null
      this.isEdit = false
      this.applicationId = ''
      this.applyStatus = null
      this.enrollStatus = null
      this.rejectFromStatus = 0
      this.auditRemark = ''
      this.checkExisting()
    },
    onEflSourceChange() {
      this.form.eflType = ''
      this.form.eflScore = ''
      this.form.eflExamDate = ''
      this.form.eflCertificateNo = ''
      this.form.eflExamAddress = ''
      this.form.achievementProofUrl = ''
      this.form.achievementProofName = ''
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    onUpload(e, key) {
      this.form[`${key}Url`] = e.url || e.fullpath || ''
      this.form[`${key}Name`] = e.name || e.filename || ''
      if (this.$refs.form) {
        this.$refs.form.validateField(`${key}Url`)
      }
    },
    onImageSuccess(e, key) {
      this.form[`${key}Url`] = e.url || ''
      this.form[`${key}Name`] = e.name || ''
      if (this.$refs.form) {
        this.$refs.form.validateField(`${key}Url`)
      }
    },
    onCancel() {
      this.$router.back()
    },
    buildSubmitParams() {
      const params = {
        thesisBatchId: this.batchId,
        examAvgScore: this.form.examAvgScore,
        studentStatusProofUrl: this.form.studentStatusProofUrl,
        studentStatusProofName: this.form.studentStatusProofName,
        eflSource: this.form.eflSource,
        eflType: this.form.eflSource === 1 ? this.form.eflType : '',
        eflScore: this.form.eflSource === 1 ? this.form.eflScore : '',
        eflExamDate: this.form.eflExamDate,
        eflCertificateNo: this.form.eflSource === 1 ? this.form.eflCertificateNo : '',
        eflExamAddress: this.form.eflSource === 1 ? this.form.eflExamAddress : '',
        achievementProofUrl: this.form.achievementProofUrl,
        achievementProofName: this.form.achievementProofName,
        thesisTitle: this.form.thesisTitle,
        plagiarismRate: this.form.plagiarismRate,
        thesisManuscriptUrl: this.form.thesisManuscriptUrl,
        thesisManuscriptName: this.form.thesisManuscriptName,
        plagiarismReportUrl: this.form.plagiarismReportUrl,
        plagiarismReportName: this.form.plagiarismReportName,
        otherAttachmentUrl: this.form.otherAttachmentUrl,
        otherAttachmentName: this.form.otherAttachmentName,
        electronicPhotoUrl: this.form.electronicPhotoUrl,
        electronicPhotoName: this.form.electronicPhotoName,
      }
      if (this.applicationId) params.id = this.applicationId
      return params
    },
    doSubmit() {
      this.submitting = true
      const params = this.buildSubmitParams()
      this.$http
        .post('/student/thesis/degree/application/submit', params)
        .then(() => {
          this.$message.success(this.isEdit ? '重新提交成功' : '提交成功')
          this.$router.replace({
            path: '/main/degreeApplyResult',
            query: { batchId: this.batchId },
          })
        })
        .finally(() => {
          this.submitting = false
        })
    },
    onSubmit() {
      if (!this.isInSubmitTime) {
        this.$message.warning(
          `${this.submitTimeLabel}为：${this.submitTimeText}，当前不在提交时间范围内`
        )
        return
      }
      this.$refs.form.validate((valid) => {
        if (!valid) return
        if (!this.batchId) {
          this.$message.warning('请选择学位申请批次')
          return
        }
        this.doSubmit()
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/variable.scss";

.degree-apply {
  min-height: 500px;

  ::v-deep .el-input__inner {
    height: 36px;
    line-height: 36px;
  }
}

.page-header {
  background-color: #fff;
  border-radius: 5px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  height: 72px;
  line-height: 72px;
}

.page-header-line {
  width: 4px;
  height: 22px;
  background-color: $primary;
  border-radius: 2px;
  margin-right: 10px;
  flex-shrink: 0;
}

.page-header h5 {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0;
  line-height: 60px;
}

.page-body {
  background-color: #fff;
  border-radius: 5px;
  padding: 20px 24px 32px;
}

.batch-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 40px;
}

.batch-item {
  display: flex;
  align-items: center;
}

.batch-select {
  width: 280px;
  max-width: 100%;
}

.batch-time {
  margin-left: auto;
}

.time-tip {
  margin-top: 12px;
  color: #e6a23c;
  font-size: 13px;
  line-height: 20px;
}

.submit-time-empty {
  padding: 60px 0 40px;
}

.field-label {
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.required-label::before {
  content: "*";
  color: #f56c6c;
  margin-right: 4px;
}

.field-value {
  color: #333;
  font-size: 14px;
}

.audit-result-box {
  margin-top: 16px;
  padding: 14px 16px;
  background: #fff7f7;
  border: 1px solid #fde2e2;
  border-radius: 5px;
  border-left: 3px solid #f56c6c;
}

.audit-result-row {
  display: flex;
  align-items: flex-start;
  line-height: 22px;
}

.audit-result-row + .audit-result-row {
  margin-top: 8px;
}

.audit-label {
  flex-shrink: 0;
  color: #767676;
  font-size: 14px;
  line-height: 22px;
}

.status-text {
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.status-fail {
  color: #f56c6c;
}

.audit-desc {
  flex: 1;
  min-width: 0;
  color: #1a1a1a;
  font-size: 14px;
  line-height: 22px;
  word-break: break-all;
  white-space: pre-wrap;
}

.divider {
  height: 1px;
  background-color: #e8e8e8;
  margin: 20px 0;
}

.section-title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 18px;
}

.apply-form {
  ::v-deep .el-form-item__label {
    color: #333;
    font-weight: 400;
  }

  ::v-deep .el-form-item {
    margin-bottom: 18px;
  }
}

.radio-item {
  ::v-deep .el-form-item__content {
    margin-left: 0 !important;
  }
}

.form-input {
  width: 320px;
  max-width: 100%;
}

.form-input-full {
  width: 100%;
}

.form-input-wide {
  width: 100%;
  max-width: 720px;
}

.upload-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.upload-tip {
  font-size: 13px;
  color: #999;
  line-height: 20px;
}

.file-link {
  display: inline-block;
  margin-top: 6px;
  color: $primary;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.action-row {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 28px;
  padding-top: 8px;
}

@media (max-width: 768px) {
  .batch-time {
    margin-left: 0;
  }

  .form-input,
  .form-input-wide {
    width: 100%;
  }
}
</style>
